import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Mail, KeyRound, Lock, AlertCircle, CheckCircle2, Send, LogIn, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState("email"); // email | code | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const requestCode = async () => {
    setErr(""); setInfo("");
    if (!email) { setErr("Ingresá tu correo"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/auth/request-password-reset", { email });
      setInfo(data?.message || "Si existe una cuenta con ese correo, te enviamos un código.");
      setStep("code");
      setResendCooldown(60);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo("");
    if (step === "email") return requestCode();
    if (!code || code.length < 6) { setErr("Ingresá el código de 6 dígitos"); return; }
    if (newPassword.length < 6) { setErr("La contraseña debe tener al menos 6 caracteres"); return; }
    if (newPassword !== confirmPassword) { setErr("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: newPassword });
      setStep("done");
      setInfo("¡Contraseña actualizada! Redirigiendo al inicio de sesión…");
      setTimeout(() => nav("/login"), 2000);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bg)]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary-c text-white">
        <div>
          <img src="/vertice-logo.png" alt="Vértice" className="h-14 w-auto brightness-0 invert" />
        </div>
        <div>
          <h1 className="font-display text-5xl font-semibold leading-tight mb-4">Recuperá el acceso.</h1>
          <p className="text-lg opacity-90 max-w-md">Te enviamos un código al correo con el que creaste tu cuenta. Ingresálo aquí y elegí una nueva contraseña.</p>
        </div>
        <div className="text-xs opacity-70">© Grupo Vértice CR · Sistema de Recursos Humanos</div>
      </div>

      <div className="flex flex-col items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <img src="/vertice-logo.png" alt="Vértice" className="h-10 w-auto" />
            </div>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-secondary-c hover:text-primary-c mb-3" data-testid="back-to-login">
              <ArrowLeft size={14} /> Volver al inicio de sesión
            </Link>
            <h2 className="font-display text-3xl font-semibold mb-1.5">Restablecer contraseña</h2>
            <p className="text-sm text-secondary-c">
              {step === "email" ? "Te enviaremos un código de verificación a tu correo." :
               step === "code" ? "Ingresá el código y tu nueva contraseña." :
               "¡Listo!"}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="np-label">Correo electrónico</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  className="np-input pl-9"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="tu@empresa.cr"
                  disabled={step !== "email"}
                />
              </div>
            </div>

            {step === "code" && (
              <>
                <div>
                  <label className="np-label"><KeyRound size={12} className="inline mr-1" /> Código de verificación</label>
                  <div className="relative">
                    <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
                    <input
                      data-testid="forgot-code-input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      className="np-input pl-9 tracking-widest text-lg text-center font-mono"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      required
                      placeholder="123456"
                    />
                  </div>
                  <p className="text-[11px] text-secondary-c mt-1">
                    Revisá tu correo. El código vence en 10 minutos.{" "}
                    {resendCooldown > 0 ? (
                      <span className="text-secondary-c">Podés reenviar en {resendCooldown}s</span>
                    ) : (
                      <button type="button" onClick={requestCode} disabled={sending} className="text-accent-color font-semibold hover:underline" data-testid="forgot-resend-btn">
                        {sending ? "Enviando..." : "Reenviar código"}
                      </button>
                    )}
                  </p>
                </div>

                <div>
                  <label className="np-label"><Lock size={12} className="inline mr-1" /> Nueva contraseña</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
                    <input
                      data-testid="forgot-new-password"
                      type="password"
                      className="np-input pl-9"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      placeholder="Mínimo 6 caracteres"
                      minLength={6}
                    />
                  </div>
                </div>

                <div>
                  <label className="np-label"><Lock size={12} className="inline mr-1" /> Confirmar contraseña</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
                    <input
                      data-testid="forgot-confirm-password"
                      type="password"
                      className="np-input pl-9"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="Repetí la contraseña"
                      minLength={6}
                    />
                  </div>
                </div>
              </>
            )}

            {info && <div data-testid="forgot-info" className="flex items-start gap-2 p-3 rounded-xl bg-[#E6EFEA] text-primary-c text-sm"><CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> {info}</div>}
            {err && <div data-testid="forgot-error" className="flex items-center gap-2 p-3 rounded-xl bg-[#FDEDED] text-[#B84B4B] text-sm"><AlertCircle size={16} /> {err}</div>}

            {step !== "done" && (
              <button
                type="submit"
                disabled={loading || sending}
                className="np-btn np-btn-primary w-full"
                data-testid="forgot-submit-btn"
              >
                {loading || sending ? "Cargando..." : (
                  step === "email"
                    ? (<><Send size={16} /> Enviar código</>)
                    : (<><LogIn size={16} /> Restablecer contraseña</>)
                )}
              </button>
            )}
          </form>

          <div className="text-center text-sm mt-6 text-secondary-c">
            ¿Ya te acordás? <Link to="/login" className="text-accent-color font-semibold hover:underline" data-testid="forgot-goto-login">Iniciar sesión</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
